/**
 * The finance assistant.
 *
 * ═══════════════════════════════════════════════════════════════════
 * THE ASSISTANT READS. IT DOES NOT POST, APPROVE OR PAY.
 *
 * Every answer is produced by running a deterministic SQL query against the
 * data the ASKING USER is permitted to see, and the answer shows the figures it
 * used. It is a faster way to ask a question you could have asked with a
 * report, not an oracle.
 *
 * It cannot create a journal, approve a requisition, release a payment or change
 * a supplier's bank details — not because it is told not to, but because those
 * capabilities are not wired to it at all.
 *
 * The anomaly rules are the same: they raise a prompt for a person to look at.
 * None of them takes an action.
 * ═══════════════════════════════════════════════════════════════════
 */

import { useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { useAsync, useMutation } from '../lib/useAsync';
import { dateTime, money, relativeTime, statusLabel, statusTone } from '../lib/format';
import { Alert, AsyncPanel, Badge, Card, DataTable, EmptyState, ErrorNotice, Money, PageHeader, Tabs } from '../components/ui';

interface AssistantAnswer {
  intent: string;
  answer: string;
  figures?: Array<{ label: string; valueMinor?: number; value?: string }>;
  rows?: Array<Record<string, unknown>>;
  basis?: string;
  provider?: string;
}

interface Anomaly {
  id: string;
  rule_code: string;
  title: string;
  description: string;
  severity: string;
  status: string;
  subject_reference: string | null;
  detected_at: string;
  evidence: Record<string, unknown> | null;
}

export function AssistantPage() {
  const [tab, setTab] = useState('ask');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<AssistantAnswer | null>(null);

  const examples = useAsync(
    () => api.get<{ intents: Array<{ intent: string; example: string; description: string }>; provider: string }>(
      '/self-service/assistant/examples',
    ),
    [],
  );

  const anomalies = useAsync(() => api.get<{ anomalies: Anomaly[] }>('/self-service/anomalies'), []);

  const ask = useMutation(async (text: string) =>
    api.post<AssistantAnswer>('/self-service/assistant/ask', { question: text }),
  );

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    void ask.run(question).then((r) => r && setAnswer(r));
  };

  return (
    <>
      <PageHeader
        title="Finance assistant"
        subtitle="Ask a question about your own figures. It reads; it never posts, approves or pays."
      />

      <Alert tone="info" title="What this can and cannot do">
        <p>
          Every answer comes from a deterministic query against the data you are permitted to see, and shows the
          figures it used. The assistant has no ability to create a journal, approve anything, release a payment or
          change a supplier's bank details — those capabilities are not connected to it.
        </p>
      </Alert>

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { key: 'ask', label: 'Ask' },
          { key: 'anomalies', label: 'Things worth a look', badge: anomalies.data?.anomalies.length },
        ]}
      />

      {tab === 'ask' ? (
        <div className="grid grid--3">
          <div style={{ gridColumn: 'span 2' }} className="stack">
            <Card title="Ask a question">
              <form onSubmit={onSubmit} className="stack">
                <label className="field">
                  <span className="field__label">Your question</span>
                  <input
                    type="text"
                    value={question}
                    onChange={(e) => setQuestion(e.currentTarget.value)}
                    placeholder="How much is left in my budget?"
                  />
                </label>
                <button type="submit" className="btn btn--primary" disabled={ask.busy || question.trim().length < 3}>
                  {ask.busy ? 'Working it out…' : 'Ask'}
                </button>
                <ErrorNotice error={ask.error} />
              </form>
            </Card>

            {answer ? (
              <Card title="Answer" subtitle={answer.provider ? `Produced locally by the ${answer.provider} engine` : undefined}>
                <p>{answer.answer}</p>

                {answer.figures && answer.figures.length > 0 ? (
                  <div className="grid grid--3" style={{ marginTop: '1rem' }}>
                    {answer.figures.map((f) => (
                      <div key={f.label} className="stat">
                        <div className="stat__label">{f.label}</div>
                        <div className="stat__value">
                          {f.valueMinor !== undefined ? money(f.valueMinor) : f.value}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                {answer.rows && answer.rows.length > 0 ? (
                  <div style={{ marginTop: '1rem' }}>
                    <DataTable
                      caption="The figures behind the answer"
                      rows={answer.rows}
                      rowKey={(_r, i) => String(i)}
                      columns={Object.keys(answer.rows[0]!).map((key) => ({
                        key,
                        header: statusLabel(key.replace(/Minor$/, '')),
                        numeric: key.toLowerCase().includes('minor'),
                        render: (row: Record<string, unknown>) =>
                          key.toLowerCase().includes('minor') ? (
                            <Money minor={Number(row[key])} />
                          ) : (
                            String(row[key] ?? '—')
                          ),
                      }))}
                    />
                  </div>
                ) : null}

                {answer.basis ? (
                  <p className="smaller muted" style={{ marginTop: '1rem' }}>
                    <strong>How this was worked out.</strong> {answer.basis}
                  </p>
                ) : null}
              </Card>
            ) : null}
          </div>

          <Card title="Things it can answer" subtitle="It recognises these questions; anything else it says so.">
            <AsyncPanel state={examples}>
              {(data) => (
                <ul className="stack stack--sm">
                  {data.intents.map((i) => (
                    <li key={i.intent}>
                      <button
                        type="button"
                        className="btn btn--ghost btn--block"
                        onClick={() => {
                          setQuestion(i.example);
                          void ask.run(i.example).then((r) => r && setAnswer(r));
                        }}
                      >
                        <span className="smaller">{i.example}</span>
                      </button>
                      <span className="smaller muted">{i.description}</span>
                    </li>
                  ))}
                </ul>
              )}
            </AsyncPanel>
          </Card>
        </div>
      ) : (
        <Card
          title="Things worth a look"
          subtitle="Rule-based checks over posted data. Each one is a prompt to a person; none of them takes an action."
          flush
        >
          <AsyncPanel state={anomalies}>
            {(data) =>
              data.anomalies.length === 0 ? (
                <div style={{ padding: '1.5rem' }}>
                  <EmptyState title="Nothing has been flagged" icon="✓" />
                </div>
              ) : (
                <DataTable
                  caption="Anomalies detected"
                  rows={data.anomalies}
                  rowKey={(a) => a.id}
                  columns={[
                    {
                      key: 'what',
                      header: 'What was noticed',
                      render: (a) => (
                        <>
                          <div className="strong">{a.title}</div>
                          <div className="smaller muted">{a.description}</div>
                        </>
                      ),
                    },
                    { key: 'rule', header: 'Rule', render: (a) => <span className="mono smaller">{a.rule_code}</span> },
                    {
                      key: 'subject',
                      header: 'About',
                      render: (a) => <span className="mono smaller">{a.subject_reference ?? '—'}</span>,
                    },
                    {
                      key: 'severity',
                      header: 'Severity',
                      render: (a) => <Badge tone={statusTone(a.severity)}>{statusLabel(a.severity)}</Badge>,
                    },
                    {
                      key: 'when',
                      header: 'Noticed',
                      render: (a) => <span className="smaller muted">{relativeTime(a.detected_at)}</span>,
                    },
                    { key: 'status', header: 'Status', render: (a) => <Badge tone={statusTone(a.status)}>{statusLabel(a.status)}</Badge> },
                  ]}
                />
              )
            }
          </AsyncPanel>
        </Card>
      )}
    </>
  );
}
